import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { MapPin, Phone, Clock, Navigation, Car, Train } from 'lucide-react';

const stores = [
  {
    id: 1,
    name: 'NutraFuel - Panitanki',
    address: 'NutraFuel Supplements Store – Panitanki (Indo-Nepal Border), Mechi Market, Custom Office, India-Nepal Border, Siliguri, West Bengal',
    phone: '+977-9824042067',
    email: 'Nutrafuelsupplements@gmail.com',
    timings: 'Mon-Sun: 9:00 AM - 7:00 PM',
    mapUrl: 'https://maps.google.com/maps?q=Panitanki+Mechi+Market+Custom+Office+Indo+Nepal+Border&t=&z=15&ie=UTF8&iwloc=&output=embed',
    directions: [
      'Located at Mechi Market, Indo-Nepal Border',
      'Near Custom Office at Panitanki',
      'Easy access from both India and Nepal side',
    ],
    features: ['Border Location', 'Nepal Customers Welcome', 'Bulk Orders', 'Customs Guidance'],
  },
];

const StoresPage = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 md:pt-40 pb-16 bg-gradient-to-b from-muted/50 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-gold font-semibold text-sm uppercase tracking-wider mb-4">VISIT US</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-foreground leading-tight mb-6">
              Store Locations
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Visit our physical store at Panitanki. See products before you buy, get expert guidance, and verify authenticity in person.
            </p>
          </div>
        </div>
      </section>

      {/* Stores Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="space-y-12">
            {stores.map((store, index) => (
              <Card key={store.id} className="overflow-hidden border-0 shadow-elevated">
                <div className="grid lg:grid-cols-2">
                  {/* Map */}
                  <div className={`h-64 lg:h-auto min-h-[400px] bg-muted ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <iframe
                      src={store.mapUrl}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title={`Map for ${store.name}`}
                    />
                  </div>

                  {/* Details */}
                  <CardContent className="p-8 lg:p-12">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold/20 text-gold text-sm font-medium mb-4">
                      <MapPin className="w-4 h-4" />
                      Store {index + 1}
                    </div>
                    <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-6">{store.name}</h2>

                    <div className="space-y-4 mb-8">
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-foreground">Address</p>
                          <p className="text-muted-foreground">{store.address}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Phone className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-foreground">Phone</p>
                          <a href={`tel:${store.phone}`} className="text-primary hover:text-gold transition-colors">{store.phone}</a>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-foreground">Store Hours</p>
                          <p className="text-muted-foreground">{store.timings}</p>
                        </div>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="flex flex-wrap gap-2 mb-8">
                      {store.features.map((feature) => (
                        <span key={feature} className="text-xs px-3 py-1 rounded-full bg-primary/10 text-primary">
                          {feature}
                        </span>
                      ))}
                    </div>

                    {/* Directions */}
                    <div className="p-4 rounded-xl bg-muted mb-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Navigation className="w-4 h-4 text-gold" />
                        <span className="font-medium text-foreground">How to Reach</span>
                      </div>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        {store.directions.map((direction, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-primary">•</span>
                            {direction}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* CTAs */}
                    <div className="flex flex-wrap gap-3">
                      <Button variant="default" asChild>
                        <a href={`tel:${store.phone}`}>
                          <Phone className="w-4 h-4 mr-2" />
                          Call Store
                        </a>
                      </Button>
                      <Button variant="outline" asChild>
                        <a href={`https://www.google.com/maps/search/${encodeURIComponent(store.address)}`} target="_blank" rel="noopener noreferrer">
                          <Navigation className="w-4 h-4 mr-2" />
                          Get Directions
                        </a>
                      </Button>
                    </div>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Travel Tips */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="section-title text-foreground">Getting Here</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <Card className="bg-card border-0 shadow-soft">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Car className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">By Road</h3>
                </div>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Well connected via NH 31</li>
                  <li>• Ample parking available at both stores</li>
                  <li>• Easy access from Siliguri town</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-card border-0 shadow-soft">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Train className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">By Rail/Air</h3>
                </div>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• NJP Railway Station: 10 km from Siliguri store</li>
                  <li>• Bagdogra Airport: 15 km from Siliguri store</li>
                  <li>• Taxi/auto easily available</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 hero-gradient">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl sm:text-5xl font-display font-bold text-primary-foreground mb-6">
              Can't Visit the Store?
            </h2>
            <p className="text-lg text-primary-foreground/80 mb-8">
              No problem! We deliver across India and Nepal. Order on WhatsApp and get authentic supplements delivered to your doorstep.
            </p>
            <Button variant="whatsapp" size="xl" asChild>
              <a href="https://wa.me/9779824042067" target="_blank" rel="noopener noreferrer">
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current mr-2">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Order on WhatsApp
              </a>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default StoresPage;
